import { type UserRole, useAuthStore } from '@/stores/auth';

/**
 * Admin access levels for the Admin Panel
 */
export type AdminAccessLevel = 'full' | 'readonly' | 'none';

/**
 * Roles that can access the Admin Panel
 */
const ADMIN_PANEL_ROLES: UserRole[] = ['BCT_ADMIN', 'BCT_PM'];

/**
 * Role that has full CRUD access
 */
const FULL_ACCESS_ROLE: UserRole = 'BCT_ADMIN';

/**
 * Hook for checking admin panel access permissions
 *
 * @returns {Object} Admin access state and helpers
 * - canAccess: boolean - Whether the user can access the Admin Panel
 * - accessLevel: AdminAccessLevel - 'full', 'readonly', or 'none'
 * - isFullAccess: boolean - Whether the user has full CRUD access
 * - isReadOnly: boolean - Whether the user has read-only access
 * - user: User | null - The current authenticated user
 */
export function useAdminAccess() {
  const { user } = useAuthStore();

  const canAccess = user ? ADMIN_PANEL_ROLES.includes(user.role) : false;
  const isFullAccess = user?.role === FULL_ACCESS_ROLE;
  const isReadOnly = canAccess && !isFullAccess;

  let accessLevel: AdminAccessLevel = 'none';
  if (isFullAccess) {
    accessLevel = 'full';
  } else if (isReadOnly) {
    accessLevel = 'readonly';
  }

  return {
    canAccess,
    accessLevel,
    isFullAccess,
    isReadOnly,
    user,
  };
}

/**
 * Check if a role has admin panel access
 * Useful for conditional rendering without the hook
 */
export function hasAdminAccess(role: UserRole | undefined): boolean {
  return role ? ADMIN_PANEL_ROLES.includes(role) : false;
}
